import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Video,
  Phone,
  Building2,
  Clock,
  User,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { formatDate, getInitials } from '@/lib/utils'

interface Interview {
  id: string
  title: string
  type: 'phone' | 'video' | 'onsite'
  scheduledAt: string | number
  duration: number
  status: 'scheduled' | 'completed' | 'cancelled'
  application: {
    id: string
    candidate: {
      id: string
      name: string
      avatarUrl?: string
    }
    job: {
      id: string
      title: string
    }
  }
  interviewers: {
    id: string
    name: string
    avatarUrl?: string
  }[]
}

interface InterviewCalendarProps {
  interviews: Interview[]
  onInterviewClick?: (interview: Interview) => void
}

type ViewMode = 'day' | 'week' | 'month'

const typeIcons = {
  phone: Phone,
  video: Video,
  onsite: Building2,
}

const statusColors = {
  scheduled: 'bg-primary text-primary-foreground',
  completed: 'bg-success text-white',
  cancelled: 'bg-muted text-muted-foreground line-through',
}

export function InterviewCalendar({
  interviews,
  onInterviewClick,
}: InterviewCalendarProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('week')
  const [currentDate, setCurrentDate] = useState(new Date())

  const navigate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate)
    switch (viewMode) {
      case 'day':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1))
        break
      case 'week':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7))
        break
      case 'month':
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1))
        break
    }
    setCurrentDate(newDate)
  }

  const goToToday = () => setCurrentDate(new Date())

  const getDateRange = () => {
    const start = new Date(currentDate)
    const end = new Date(currentDate)

    switch (viewMode) {
      case 'day':
        return { start, end }
      case 'week':
        const day = start.getDay()
        start.setDate(start.getDate() - day)
        end.setDate(end.getDate() + (6 - day))
        return { start, end }
      case 'month':
        start.setDate(1)
        end.setMonth(end.getMonth() + 1, 0)
        return { start, end }
    }
  }

  const { start: rangeStart, end: rangeEnd } = getDateRange()

  const getHeaderText = () => {
    const options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' }
    switch (viewMode) {
      case 'day':
        return currentDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      case 'week':
        return `${rangeStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${rangeEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
      case 'month':
        return currentDate.toLocaleDateString('en-US', options)
    }
  }

  const filteredInterviews = useMemo(() => {
    return interviews.filter((interview) => {
      const date = new Date(interview.scheduledAt)
      return date >= rangeStart && date <= rangeEnd
    })
  }, [interviews, rangeStart, rangeEnd])

  const getDaysInView = () => {
    const days: Date[] = []
    const current = new Date(rangeStart)
    while (current <= rangeEnd) {
      days.push(new Date(current))
      current.setDate(current.getDate() + 1)
    }
    return days
  }

  const getInterviewsForDay = (date: Date) => {
    return filteredInterviews.filter((interview) => {
      const interviewDate = new Date(interview.scheduledAt)
      return (
        interviewDate.getDate() === date.getDate() &&
        interviewDate.getMonth() === date.getMonth() &&
        interviewDate.getFullYear() === date.getFullYear()
      )
    })
  }

  const isToday = (date: Date) => {
    const today = new Date()
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    )
  }

  return (
    <Card className="flex flex-col flex-1 min-h-0">
      <CardHeader className="flex-shrink-0 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Interviews
          </CardTitle>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            <div className="flex border rounded-lg overflow-hidden">
              {(['day', 'week', 'month'] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`
                    px-3 py-1.5 text-sm font-medium transition-colors
                    ${viewMode === mode ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}
                  `}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => navigate('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={() => navigate('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Today
            </Button>
          </div>
          <h3 className="font-semibold">{getHeaderText()}</h3>
          <div className="w-24" /> {/* Spacer for alignment */}
        </div>
      </CardHeader>

      <CardContent className="flex-1 min-h-0 flex flex-col pt-0">
        {viewMode === 'month' ? (
          <MonthView
            days={getDaysInView()}
            interviews={filteredInterviews}
            getInterviewsForDay={getInterviewsForDay}
            isToday={isToday}
            onInterviewClick={onInterviewClick}
          />
        ) : viewMode === 'week' ? (
          <WeekView
            days={getDaysInView()}
            getInterviewsForDay={getInterviewsForDay}
            isToday={isToday}
            onInterviewClick={onInterviewClick}
          />
        ) : (
          <DayView
            date={currentDate}
            interviews={getInterviewsForDay(currentDate)}
            onInterviewClick={onInterviewClick}
          />
        )}
      </CardContent>
    </Card>
  )
}

// Month View
function MonthView({
  days,
  getInterviewsForDay,
  isToday,
  onInterviewClick,
}: {
  days: Date[]
  interviews: Interview[]
  getInterviewsForDay: (date: Date) => Interview[]
  isToday: (date: Date) => boolean
  onInterviewClick?: (interview: Interview) => void
}) {
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  // Pad days to start on the correct weekday
  const startPadding = days[0].getDay()
  const paddedDays = [...Array(startPadding).fill(null), ...days]
  const rows = Math.ceil(paddedDays.length / 7)

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="grid grid-cols-7 flex-shrink-0">
        {weekDays.map((day) => (
          <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className={`grid grid-cols-7 gap-px flex-1 min-h-0`} style={{ gridTemplateRows: `repeat(${rows}, 1fr)` }}>
        {paddedDays.map((date, i) => {
          if (!date) return <div key={`pad-${i}`} className="border border-border/30 bg-muted/20 rounded" />
          const dayInterviews = getInterviewsForDay(date)
          return (
            <div
              key={date.toISOString()}
              className={`
                p-1 border rounded overflow-hidden
                ${isToday(date) ? 'bg-primary/5 border-primary' : 'border-border/50'}
              `}
            >
              <div
                className={`
                  text-xs font-medium
                  ${isToday(date) ? 'text-primary' : 'text-muted-foreground'}
                `}
              >
                {date.getDate()}
              </div>
              <div className="space-y-0.5 mt-0.5">
                {dayInterviews.slice(0, 2).map((interview) => (
                  <InterviewChip
                    key={interview.id}
                    interview={interview}
                    onClick={() => onInterviewClick?.(interview)}
                    compact
                  />
                ))}
                {dayInterviews.length > 2 && (
                  <div className="text-[10px] text-muted-foreground">
                    +{dayInterviews.length - 2} more
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Week View with Time Grid
function WeekView({
  days,
  getInterviewsForDay,
  isToday,
  onInterviewClick,
}: {
  days: Date[]
  getInterviewsForDay: (date: Date) => Interview[]
  isToday: (date: Date) => boolean
  onInterviewClick?: (interview: Interview) => void
}) {
  const hours = Array.from({ length: 10 }, (_, i) => i + 8) // 8 AM to 5 PM

  const getInterviewsAtHour = (date: Date, hour: number) => {
    const dayInterviews = getInterviewsForDay(date)
    return dayInterviews.filter((interview) => {
      const interviewHour = new Date(interview.scheduledAt).getHours()
      return interviewHour === hour
    })
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header with day names */}
      <div className="grid grid-cols-[48px_repeat(7,1fr)] gap-px flex-shrink-0">
        <div /> {/* Empty cell for time column */}
        {days.map((date) => (
          <div
            key={date.toISOString()}
            className={`
              text-center py-1 rounded
              ${isToday(date) ? 'bg-primary text-primary-foreground' : ''}
            `}
          >
            <div className={`text-[10px] ${isToday(date) ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
              {date.toLocaleDateString('en-US', { weekday: 'short' })}
            </div>
            <div className="text-sm font-semibold">{date.getDate()}</div>
          </div>
        ))}
      </div>

      {/* Time grid */}
      <div className="grid grid-cols-[48px_repeat(7,1fr)] gap-px flex-1 min-h-0" style={{ gridTemplateRows: `repeat(${hours.length}, 1fr)` }}>
        {hours.map((hour) => (
          <div key={hour} className="contents">
            {/* Time label */}
            <div className="text-[10px] text-muted-foreground pr-1 text-right flex items-start pt-0.5">
              <span className="ml-auto">{hour === 12 ? '12p' : hour > 12 ? `${hour - 12}p` : `${hour}a`}</span>
            </div>
            {/* Day cells for this hour */}
            {days.map((date) => {
              const hourInterviews = getInterviewsAtHour(date, hour)
              return (
                <div
                  key={`${date.toISOString()}-${hour}`}
                  className={`
                    border-t border-border/30 overflow-hidden
                    ${isToday(date) ? 'bg-primary/5' : ''}
                  `}
                >
                  {hourInterviews.map((interview) => (
                    <InterviewChip
                      key={interview.id}
                      interview={interview}
                      onClick={() => onInterviewClick?.(interview)}
                      compact
                    />
                  ))}
                </div>
              )
            })}
          </div>
        ))}
      </div>

      {/* Empty state hint */}
      {days.every(date => getInterviewsForDay(date).length === 0) && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-muted-foreground">
            <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No interviews scheduled this week</p>
          </div>
        </div>
      )}
    </div>
  )
}

// Day View
function DayView({
  date,
  interviews,
  onInterviewClick,
}: {
  date: Date
  interviews: Interview[]
  onInterviewClick?: (interview: Interview) => void
}) {
  const hours = Array.from({ length: 12 }, (_, i) => i + 8) // 8 AM to 7 PM

  const getInterviewsAtHour = (hour: number) => {
    return interviews.filter((interview) => {
      const interviewHour = new Date(interview.scheduledAt).getHours()
      return interviewHour === hour
    })
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {hours.map((hour) => {
        const hourInterviews = getInterviewsAtHour(hour)
        return (
          <div key={hour} className="flex gap-3 flex-1 min-h-0">
            <div className="w-12 text-[10px] text-muted-foreground pt-0.5 text-right flex-shrink-0">
              {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
            </div>
            <div className="flex-1 border-t border-border/30 overflow-hidden">
              {hourInterviews.map((interview) => (
                <InterviewCard
                  key={interview.id}
                  interview={interview}
                  onClick={() => onInterviewClick?.(interview)}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Interview Chip (compact)
function InterviewChip({
  interview,
  onClick,
  compact = false,
}: {
  interview: Interview
  onClick?: () => void
  compact?: boolean
}) {
  const Icon = typeIcons[interview.type]

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`
        p-2 rounded-lg cursor-pointer transition-colors
        ${statusColors[interview.status]}
      `}
    >
      <div className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        <span className={`font-medium truncate ${compact ? 'text-[10px]' : 'text-xs'}`}>
          {interview.application.candidate.name}
        </span>
      </div>
      {!compact && (
        <div className="text-[10px] opacity-80 flex items-center gap-1 mt-0.5">
          <Clock className="h-2.5 w-2.5" />
          {new Date(interview.scheduledAt).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
          })}
        </div>
      )}
    </motion.div>
  )
}

// Interview Card (full)
function InterviewCard({
  interview,
  onClick,
}: {
  interview: Interview
  onClick?: () => void
}) {
  const Icon = typeIcons[interview.type]

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={onClick}
      className="p-3 rounded-lg border bg-card hover:shadow-sm cursor-pointer transition-all"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={interview.application.candidate.avatarUrl} />
            <AvatarFallback>
              {getInitials(interview.application.candidate.name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{interview.application.candidate.name}</p>
            <p className="text-sm text-muted-foreground">
              {interview.application.job.title}
            </p>
          </div>
        </div>
        <Badge variant="secondary" className="gap-1">
          <Icon className="h-3 w-3" />
          {interview.type}
        </Badge>
      </div>

      <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {new Date(interview.scheduledAt).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
          })}
          {' • '}
          {interview.duration} min
        </span>
        {interview.interviewers.length > 0 && (
          <span className="flex items-center gap-1">
            <User className="h-3.5 w-3.5" />
            {interview.interviewers.length} interviewer(s)
          </span>
        )}
      </div>
    </motion.div>
  )
}
